import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building, MapPin, FileText, Award, CalendarCheck, Folder } from 'lucide-react';
import api from '../../api';
import Loading from '../../components/Loading/Loading';
import Alert from '../../components/Alert/Alert';
import { buildAuditClientDetailData, buildAuditClientWorkspaceUrl, getAuditClientTabs, getClientInitials } from './auditClientDetailUtils';

const AuditClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clientData, setClientData] = useState(null);
  const [activeTab, setActiveTab] = useState('detail');
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [expandedAuditProjects, setExpandedAuditProjects] = useState({});

  useEffect(() => {
    const fetchClient = async () => {
      setIsLoading(true);
      try {
        let auditWorkspace = {};
        let crmClient = {};
        try {
          const auditResponse = await api.get(buildAuditClientWorkspaceUrl(id));
          auditWorkspace = auditResponse.data || {};
        } catch (auditError) {
          console.warn('Audit workspace lookup failed, falling back to CRM data:', auditError);
        }

        const companyId = auditWorkspace.resolvedCompanyId || auditWorkspace.companyId || auditWorkspace.clientId || id;
        try {
          const crmResponse = await api.get(`/crm/leads/by-company-base/${companyId}`);
          crmClient = crmResponse.data || {};
        } catch (crmError) {
          console.warn('CRM client enrichment failed, using audit workspace payload only:', crmError);
        }

        if (!auditWorkspace.companyId && !auditWorkspace.clientId) {
          try {
            const auditResponse = await api.get(buildAuditClientWorkspaceUrl(crmClient.companyId || crmClient.clientId || id));
            auditWorkspace = auditResponse.data || {};
          } catch (auditRetryError) {
            console.warn('Audit workspace retry failed:', auditRetryError);
          }
        }

        setClientData(buildAuditClientDetailData(crmClient, {
          auditProjects: [],
          sites: [],
          certificates: [],
          auditProcesses: [],
          auditCycles: [],
          documents: [],
          ...auditWorkspace,
          companyId,
        }));
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        const diagnostics = error?.response?.data?.sectionDiagnostics;
        const status = error?.response?.status;
        setAlert({
          show: true,
          type: 'danger',
          message: backendMessage || (diagnostics ? `Backend diagnostics: ${JSON.stringify(diagnostics)}` : `Unable to load client details.${status ? ' (status: '+status+')' : ''}`),
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchClient();
  }, [id]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getDisplayValue = (value) => value || '-';
  const getArray = (value) => (Array.isArray(value) ? value : []);
  const formatDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const formatStatus = (value) => String(value || 'UNKNOWN').replace(/_/g, ' ');
  const getStatusBadgeClass = (status) => {
    const normalized = String(status || '').toUpperCase();
    if (normalized.includes('ACTIVE')) return 'bg-success-lt text-success';
    if (normalized.includes('EXPIR')) return 'bg-warning-lt text-warning';
    if (normalized.includes('COMPLETE') || normalized.includes('DONE')) return 'bg-teal-lt text-teal';
    if (normalized.includes('OPEN') || normalized.includes('PROGRESS')) return 'bg-blue-lt text-blue';
    return 'bg-secondary-lt text-secondary';
  };
  const getCompanyName = () => clientData?.companyName || clientData?.company_name || clientData?.clientName || 'Audit Client';

  const renderClientDetail = () => {
    if (!clientData) return null;

    return (
      <>
        <div className="card p-4 mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="avatar avatar-xl bg-blue text-white fs-1">{getClientInitials(getCompanyName())}</div>
            <div>
              <h3 className="mb-1">{getCompanyName()}</h3>
              <div className="text-muted">{getDisplayValue(clientData.partnerType || 'Customer / Client')}</div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h3 className="card-title mb-0">Basic information</h3>
          </div>
          <div className="card-body">
            <div className="row gy-4">
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Company Name</div>
                <div>{getDisplayValue(getCompanyName())}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Business Partner Type</div>
                <div>{getDisplayValue(clientData.partnerType)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Entity Type</div>
                <div>{getDisplayValue(clientData.entityType)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Sector</div>
                <div>{getDisplayValue(clientData.sector || clientData.sectorName || clientData.industry)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Company Size</div>
                <div>{getDisplayValue(clientData.companySize)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Website</div>
                <div>{clientData.website ? <a href={clientData.website} target="_blank" rel="noreferrer">{clientData.website}</a> : '-'}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Company Email</div>
                <div>{getDisplayValue(clientData.companyEmail || clientData.email)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Phone</div>
                <div>{getDisplayValue(clientData.phone || clientData.companyPhoneNumber)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Company Tax Number/NPWP</div>
                <div>{getDisplayValue(clientData.npwp)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Default Currency</div>
                <div>{getDisplayValue(clientData.defaultCurrency || clientData.currency)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Tagging</div>
                <div>{getDisplayValue(clientData.tagging)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Date Added</div>
                <div>{formatDate(clientData.createdAt || clientData.companyCreatedDate)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title mb-0">Address Information</h3>
          </div>
          <div className="card-body">
            <div className="row gy-4">
              <div className="col-md-4">
                <div className="text-uppercase text-muted small mb-1">Address</div>
                <div>{getDisplayValue(clientData.address || clientData.companyAddress || clientData.company_address)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Country</div>
                <div>{getDisplayValue(clientData.country || clientData.companyCountry)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">State</div>
                <div>{getDisplayValue(clientData.state)}</div>
              </div>
              <div className="col-md-2">
                <div className="text-uppercase text-muted small mb-1">City</div>
                <div>{getDisplayValue(clientData.city || clientData.companyCity)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-uppercase text-muted small mb-1">Postal Code</div>
                <div>{getDisplayValue(clientData.postalCode || clientData.postal_code)}</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderPlaceholderContent = (title, subtitle) => (
    <div className="card p-4">
      <h4 className="mb-2">{title}</h4>
      <p className="text-muted mb-0">{subtitle || `No ${title.toLowerCase()} data is available for this client yet.`}</p>
    </div>
  );

  const renderSitesContent = () => {
    const sites = getArray(clientData.sites || clientData.clientSites);
    if (sites.length === 0) {
      return renderPlaceholderContent(
        'Sites',
        'No audit site data is available yet. Sites are captured after audit configuration or project setup.'
      );
    }
    return (
      <div className="card">
        <div className="card-header">
          <div>
            <h4 className="card-title mb-1">Project Sites</h4>
            <div className="text-muted small">Sites linked to audit-relevant projects for this client. Primary sites appear first.</div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table card-table table-vcenter">
            <thead>
              <tr>
                <th>Site Name</th>
                <th>Project</th>
                <th>Address</th>
                <th>Contact</th>
                <th className="text-end">Mandays</th>
                <th>Primary</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site, index) => {
                const address = [site.address, site.city, site.state, site.country]
                  .filter(Boolean)
                  .join(', ');
                return (
                  <tr key={site.id || index}>
                    <td className="fw-semibold">
                      {getDisplayValue(site.siteName || site.name || `Site ${index + 1}`)}
                      {site.siteCode ? <div className="text-muted small">{site.siteCode}</div> : null}
                    </td>
                    <td>
                      {getDisplayValue(site.projectCode || site.projectName)}
                      {site.sourceCategory ? (
                        <div className="text-muted small">{formatStatus(site.sourceCategory)} source</div>
                      ) : null}
                    </td>
                    <td>{getDisplayValue(address || site.address || site.location)}</td>
                    <td>
                      {getDisplayValue(site.contactName)}
                      {site.contactEmail ? <div className="text-muted small">{site.contactEmail}</div> : null}
                    </td>
                    <td className="text-end">{site.auditMandays != null ? Number(site.auditMandays).toFixed(2) : '-'}</td>
                    <td>
                      {site.isPrimary ? <span className="badge bg-success">Primary</span> : <span className="text-muted">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCertificateContent = () => {
    const certificates = getArray(clientData.certificates || clientData.certificateHistory);
    const certificateSites = getArray(clientData.certificateSites);
    if (certificates.length > 0) {
      return (
        <div className="d-flex flex-column gap-3">
          <div className="card">
            <div className="card-header">
              <div>
                <h4 className="card-title mb-1">Certificate Monitoring</h4>
                <div className="text-muted small">Certificate records aligned with Certificate Monitoring for this audit client.</div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table card-table table-vcenter">
                <thead>
                  <tr>
                    <th>Certificate Number</th>
                    <th>Client Name</th>
                    <th>Standard</th>
                    <th>Issue Date</th>
                    <th>Expiry Date</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((certificate, index) => {
                    const detailRoute = certificate.detailRoute || (certificate.projectId || certificate.auditId ? `/audit-process/${certificate.projectId || certificate.auditId}` : null);
                    return (
                      <tr key={certificate.id || index}>
                        <td className="fw-semibold">{getDisplayValue(certificate.certificateNumber || certificate.number)}</td>
                        <td>{getDisplayValue(certificate.clientName || certificate.companyName || getCompanyName())}</td>
                        <td>{getDisplayValue(certificate.standardName || certificate.name || certificate.standard)}</td>
                        <td>{formatDate(certificate.issueDate || certificate.issuedAt || certificate.issuedDate)}</td>
                        <td>{formatDate(certificate.expiryDate || certificate.expiresAt)}</td>
                        <td><span className={`badge ${getStatusBadgeClass(certificate.status)}`}>{formatStatus(certificate.status)}</span></td>
                        <td className="text-end">
                          <button type="button" className="btn btn-sm btn-outline-primary" disabled={!detailRoute} onClick={() => detailRoute && navigate(detailRoute)}>
                            View Project
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {certificateSites.length > 0 && (
            <div className="card">
              <div className="card-header">
                <div>
                  <h4 className="card-title mb-1">Certificate Addresses</h4>
                  <div className="text-muted small">All primary and multisite addresses attached to this client's certificate records.</div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table card-table table-vcenter">
                  <thead><tr><th>Certificate</th><th>Site</th><th>Address</th><th>Scope</th><th>Source</th></tr></thead>
                  <tbody>
                    {certificateSites.map((site, index) => (
                      <tr key={`${site.certificateId || 'certificate'}-${site.id || site.siteName || index}`}>
                        <td><div className="fw-semibold">{getDisplayValue(site.certificateNumber)}</div><div className="text-muted small">{getDisplayValue(site.certificateStandard)}</div></td>
                        <td>{getDisplayValue(site.siteName || site.name || `Site ${index + 1}`)}</td>
                        <td>{getDisplayValue([site.address, site.city, site.state, site.country].filter(Boolean).join(', '))}</td>
                        <td>{getDisplayValue(site.scope)}</td>
                        <td><span className="badge bg-secondary-lt">{formatStatus(site.sourceCategory)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    }
    return renderPlaceholderContent('Certificate', 'No certificate records are linked to this audit client yet.');
  };

  const toggleAuditProjectGroup = (groupKey) => {
    setExpandedAuditProjects((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const renderAuditContent = () => {
    const auditProjects = getArray(clientData.auditProjects || clientData.projects);
    const auditProcesses = getArray(clientData.auditProcesses);
    if (auditProjects.length > 0 || auditProcesses.length > 0) {
      const processGroups = [];
      const processGroupIndex = new Map();
      auditProcesses.forEach((process, index) => {
        const groupKey = process.projectCode || process.projectId || 'unassigned';
        if (!processGroupIndex.has(groupKey)) {
          processGroupIndex.set(groupKey, processGroups.length);
          processGroups.push({
            key: groupKey,
            projectCode: process.projectCode || null,
            clientName: process.clientName || getCompanyName(),
            tasks: [],
          });
        }
        processGroups[processGroupIndex.get(groupKey)].tasks.push({ ...process, _index: index });
      });

      return (
        <div className="row g-3">
          {processGroups.length > 0 && (
            <div className="col-12">
              <div className="d-flex flex-column gap-3">
                {processGroups.map((group) => {
                  const isExpanded = expandedAuditProjects[group.key] !== false;
                  const openCount = group.tasks.filter((t) => {
                    const normalized = String(t.status || '').toUpperCase();
                    return !normalized.includes('COMPLETE') && !normalized.includes('DONE') && !normalized.includes('CLOSED');
                  }).length;
                  return (
                    <div key={group.key} className="card">
                      <button
                        type="button"
                        className="card-header d-flex align-items-center justify-content-between w-100 border-0 bg-transparent"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleAuditProjectGroup(group.key)}
                        aria-expanded={isExpanded}
                      >
                        <div className="d-flex align-items-center gap-2 text-start">
                          <Folder size={18} className="text-muted" />
                          <div>
                            <h4 className="card-title mb-0">{getDisplayValue(group.projectCode) || 'Unassigned Project'}</h4>
                            <div className="text-muted small">{group.clientName} &middot; {group.tasks.length} task{group.tasks.length === 1 ? '' : 's'}{openCount > 0 ? `, ${openCount} open` : ''}</div>
                          </div>
                        </div>
                        <span className={`chevron ${isExpanded ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s ease' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="table-responsive">
                          <table className="table card-table table-vcenter mb-0">
                            <thead>
                              <tr>
                                <th>Stage / Task</th>
                                <th>Standard</th>
                                <th>Audit Cycle</th>
                                <th>Status</th>
                                <th>Assigned To</th>
                                <th className="text-end"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.tasks.map((process) => {
                                const detailRoute = process.detailRoute || (process.projectId ? `/audit-process/${process.projectId}` : null);
                                const stageName = process.name || process.title || 'Audit Process';
                                return (
                                  <tr key={process.id || process._index}>
                                    <td>
                                      <div className="fw-semibold">{stageName}</div>
                                      <div className="text-muted small">{process.wording || process.description || process.code || 'Audit task assigned for this client.'}</div>
                                    </td>
                                    <td>{getDisplayValue(process.standardName)}</td>
                                    <td>{getDisplayValue(process.auditCycle)}</td>
                                    <td><span className={`badge ${getStatusBadgeClass(process.status)}`}>{formatStatus(process.status || 'OPEN')}</span></td>
                                    <td>{getDisplayValue(process.assignedEmail || process.assignedTo)}</td>
                                    <td className="text-end">
                                      <div className="d-flex flex-wrap gap-2 justify-content-end">
                                        <button type="button" className="btn btn-primary" disabled={!detailRoute} onClick={() => detailRoute && navigate(detailRoute)}>
                                          View Detail
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {auditProjects.length > 0 && (
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div>
                    <h4 className="card-title mb-1">Audit Project Summary</h4>
                    <div className="text-muted small">Projects connected to this client's audit process records.</div>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table card-table table-vcenter">
                    <thead>
                      <tr>
                        <th>Project Code</th>
                        <th>Audit Type</th>
                        <th>Status</th>
                        <th>Processes</th>
                        <th>Certificates</th>
                        <th>Documents</th>
                        <th>Last Update</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditProjects.map((project, index) => (
                        <tr key={project.id || index}>
                          <td className="fw-semibold">
                            {getDisplayValue(project.projectCode)}
                            {project.isSustainabilityProject ? (
                              <span className="badge bg-green-lt text-green ms-2" title="Project carries Sustainability / ISCC services">
                                Sustainability / ISCC
                              </span>
                            ) : null}
                          </td>
                          <td>{getDisplayValue(project.auditFlaggingType || project.auditCategory || 'Standard')}</td>
                          <td><span className={`badge ${getStatusBadgeClass(project.statusDesc || project.status)}`}>{formatStatus(project.statusDesc || project.status || 'Audit Scope')}</span></td>
                          <td>{project.auditProcessCount ?? '-'}</td>
                          <td>{project.certificateCount ?? '-'}</td>
                          <td>{project.documentCount ?? '-'}</td>
                          <td>{formatDate(project.lastAuditUpdate || project.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    return renderPlaceholderContent('Audit', 'No audit process details are available yet.');
  };

  const renderCycleContent = () => {
    const cycles = getArray(clientData.auditCycles || clientData.cycles);
    if (cycles.length > 0) {
      return (
        <div className="card">
          <div className="card-header">
            <div>
              <h4 className="card-title mb-1">Audit Process Cycles</h4>
              <div className="text-muted small">Certification cycle rows are sourced from the same project service standards used by Audit Process.</div>
            </div>
          </div>
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              {cycles.map((cycle, index) => {
                const serviceLabel = [cycle.serviceName || 'Certification Program', cycle.standardName]
                  .filter(Boolean)
                  .join(' / ');
                const stageLabel = cycle.stageLabel || cycle.serviceItemName || `Cycle ${index + 1}`;
                const statusLabel = cycle.cycleStatus || (index === 0 ? 'Application Review' : 'Upcoming Audit Cycle');
                return (
                  <button
                    key={cycle.id || index}
                    type="button"
                    className="border rounded bg-white p-3 text-start w-100 d-flex justify-content-between align-items-center gap-3"
                    onClick={() => cycle.detailRoute && navigate(cycle.detailRoute)}
                    disabled={!cycle.detailRoute}
                  >
                    <span className="min-w-0">
                      <span className="d-flex flex-wrap align-items-center gap-2">
                        <span className="fw-semibold text-body">{getDisplayValue(serviceLabel)}</span>
                        <span className="badge bg-blue-lt text-blue">{getDisplayValue(stageLabel)}</span>
                        <span className="badge bg-secondary-lt text-secondary">{getDisplayValue(statusLabel)}</span>
                      </span>
                      <span className="d-block text-muted small mt-2">
                        {getDisplayValue(cycle.projectCode)} · Audit date: {formatDate(cycle.proposedDate)}
                      </span>
                    </span>
                    <span className="text-muted fs-3 lh-1">⌄</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
    return renderPlaceholderContent('Cycle', 'Audit cycle data is not configured yet.');
  };

  const renderDocumentContent = () => {
    const documents = getArray(clientData.documents || clientData.auditDocuments);
    if (documents.length > 0) {
      return (
        <div className="card">
          <div className="card-header">
            <div>
              <h4 className="card-title mb-1">Client Documents</h4>
              <div className="text-muted small">Lead, proposal, project, audit form, and evidence documents connected to this client.</div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table card-table table-vcenter">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Source</th>
                  <th>Project / Ref</th>
                  <th>Type</th>
                  <th>File</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, index) => (
                  <tr key={`${doc.sourceCategory || 'document'}-${doc.id || index}`}>
                    <td>
                      <div className="fw-semibold">{doc.originalFileName || doc.title || doc.name || `Document ${index + 1}`}</div>
                      {doc.description && <div className="text-muted small">{doc.description}</div>}
                    </td>
                    <td><span className="badge bg-blue-lt text-blue">{formatStatus(doc.sourceCategory || 'project')}</span></td>
                    <td>{doc.projectCode || doc.sourceId || '-'}</td>
                    <td><span className="badge bg-secondary-lt">{doc.documentType || doc.type || 'Attachment'}</span></td>
                    <td>
                      {doc.filePath ? (
                        <span title={doc.filePath}>{doc.fileType || doc.mimeType || 'File'}</span>
                      ) : (
                        doc.fileType || doc.mimeType || '-'
                      )}
                    </td>
                    <td>{formatDate(doc.updatedAt || doc.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return renderPlaceholderContent('Document', 'No lead, proposal, project, audit form, or audit evidence documents are linked to this client yet.');
  };

  const renderTabContent = () => {
    if (!clientData) return null;

    switch (activeTab) {
      case 'detail':
        return renderClientDetail();
      case 'sites':
        return renderSitesContent();
      case 'certificate':
        return renderCertificateContent();
      case 'audit':
        return renderAuditContent();
      case 'cycle':
        return renderCycleContent();
      case 'document':
        return renderDocumentContent();
      default:
        return renderClientDetail();
    }
  };

  if (isLoading) {
    return <Loading text="Loading Audit Client details..." />;
  }

  return (
    <div className="container-xl">
      {alert.show && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert({ show: false, type: '', message: '' })} />
      )}
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <div className="d-flex align-items-center">
              <button className="btn btn-outline-white d-none d-sm-inline-block me-3" onClick={() => navigate('/audit/clients')}>
                <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                Back
              </button>
              <div>
                <h2 className="page-title mb-1">{getCompanyName()}</h2>
                <p className="text-muted mb-0">Audit Platform client detail with audit process and certificate overview</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="card mb-4">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs">
                {getAuditClientTabs().map((tab) => {
                  const Icon = {
                    detail: Building,
                    sites: MapPin,
                    certificate: Award,
                    audit: FileText,
                    cycle: CalendarCheck,
                    document: Folder,
                  }[tab.key] || FileText;
                  return (
                    <li key={tab.key} className="nav-item-tab" role="presentation">
                      <button type="button" className={`nav-link ${activeTab === tab.key ? 'active' : ''}`} onClick={() => handleTabChange(tab.key)}>
                        <Icon className="me-2" /> {tab.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="card-body">{renderTabContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditClientDetail;
